import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { api, inr } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { useSite } from "../context/SiteContext";
import Logo from "../components/Logo";

const SERVICE_LABELS = { standard: "Standard Delivery", specialist: "Rush Hour — Vibe Coding Specialist", super: "Rush Hour — Super Vibe Coder" };

export default function Invoice() {
  const { paymentId } = useParams();
  const { format } = useCurrency();
  const { settings } = useSite();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/checkout/status/${paymentId}`).then((res) => setData(res.data)).catch(() => setError(true));
  }, [paymentId]);

  if (error) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="invoice-not-found">
        <p className="text-slate-400">Invoice not found.</p>
      </div>
    );
  }
  if (!data?.invoice) {
    return (
      <div className="pt-40 pb-32 text-center">
        <div className="w-8 h-8 border-2 border-flame border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-sm text-slate-500">{data ? "Invoice is generated once payment is confirmed." : "Loading…"}</p>
      </div>
    );
  }

  const inv = data.invoice;
  const rows = [
    ["Service", SERVICE_LABELS[inv.service] || inv.service],
    ["Development hours", `${inv.hours}h`],
    ["Hourly rate", inr(inv.hourly_rate_inr)],
    ["Base (INR)", inr(inv.base_inr)],
    ...(inv.tax_amount_inr ? [[`${inv.tax_label} (${inv.tax_rate}%)`, inr(inv.tax_amount_inr)]] : []),
    ["Total (INR)", inr(inv.total_inr)],
    ["Paid in", `${format(inv.currency_amount, inv.currency)} ${inv.currency}`],
    ...(inv.currency !== "INR" ? [["FX rate", `1 INR = ${inv.fx_rate} ${inv.currency}`], ["FX rate timestamp", inv.fx_updated_at ? new Date(inv.fx_updated_at).toLocaleString("en-IN") : "—"]] : []),
    ["Payment ID", inv.razorpay_payment_id || "—"],
    ["Order ID", inv.razorpay_order_id || "—"],
  ];

  return (
    <div className="pt-32 pb-24" data-testid="invoice-page">
      <div className="container-x max-w-3xl">
        <div className="rounded-2xl border border-white/10 bg-navy-800 p-8 sm:p-12 print:bg-white print:text-black">
          <div className="flex items-start justify-between">
            <div>
              <Logo />
              <p className="mt-4 text-xs text-slate-400">{settings.address}</p>
              <p className="text-xs text-slate-400">{settings.contact_email} · {settings.phone}</p>
            </div>
            <div className="text-right">
              <h1 className="font-heading text-3xl font-black text-white">INVOICE</h1>
              <p className="mt-2 font-mono text-sm text-flame" data-testid="invoice-number">{inv.invoice_number}</p>
              <p className="text-xs text-slate-400">{new Date(inv.issued_at).toLocaleDateString("en-IN")}</p>
              <p className="mt-1 text-xs font-bold text-emerald-400" data-testid="invoice-status">{inv.status}</p>
            </div>
          </div>

          <div className="mt-10 rounded-xl bg-navy-900/70 p-6">
            <p className="font-mono text-[9px] tracking-[0.25em] text-slate-500 mb-3">BILLED TO</p>
            <p className="font-heading font-bold text-white">{inv.customer?.name}</p>
            <p className="text-sm text-slate-400">{inv.customer?.company}</p>
            <p className="text-sm text-slate-400">{inv.customer?.email} {inv.customer?.phone && `· ${inv.customer.phone}`}</p>
            <p className="text-sm text-slate-400">{[inv.customer?.address, inv.customer?.country].filter(Boolean).join(", ")}</p>
          </div>

          <div className="mt-8 divide-y divide-white/5">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-6 py-3 text-sm">
                <span className="text-slate-500">{k}</span>
                <span className="text-slate-100 font-medium text-right">{v}</span>
              </div>
            ))}
          </div>

          <p className="mt-8 text-[10px] text-slate-500 leading-relaxed">
            Settlement to Built2Last.live occurs in INR via Razorpay per its applicable settlement process. International
            payments may be subject to card-issuer fees, foreign exchange charges or taxes imposed by the customer's bank
            or payment provider.
          </p>

          <button onClick={() => window.print()} data-testid="invoice-print-btn" className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-xs font-bold text-slate-200 hover:border-flame hover:text-flame transition-colors print:hidden">
            <Printer className="w-4 h-4" /> PRINT / SAVE PDF
          </button>
        </div>
      </div>
    </div>
  );
}
